import React, { useState, useEffect } from 'react';
import userService from '../../services/userService';
import { toast } from 'react-hot-toast';

// Get API URL from environment or use a fallback
const ClaimsList = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [expandedClaim, setExpandedClaim] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  
  useEffect(() => {
    fetchClaims();
  }, []);
  
  const fetchClaims = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching pending claims...');
      const response = await userService.getPendingClaims();
      console.log('Pending claims received:', response);
      setClaims(response);
    } catch (error) {
      console.error('Error fetching claims:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load pending claims';
      console.error('Error details:', errorMessage);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprove = async (claim) => {
    if (processing) return;
    
    try {
      setProcessing(true);
      setSelectedClaim(claim);
      
      console.log(`Approving claim for user ${claim.user_id} and organisation ${claim.organisation_id}`);
      await userService.approveProfileClaim(claim.user_id, claim.organisation_id);
      
      toast.success('Claim approved successfully');
      fetchClaims(); // Refresh the list
    } catch (error) {
      console.error('Error approving claim:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to approve claim';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
      setSelectedClaim(null);
    }
  };
  
  const handleReject = async (claim) => {
    if (processing) return;
    
    try {
      setProcessing(true);
      
      console.log(`Rejecting claim for user ${claim.user_id} and organisation ${claim.organisation_id}`);
      await userService.rejectProfileClaim(claim.user_id, claim.organisation_id, {
        reason: rejectionReason || 'Your claim was rejected by an administrator.'
      });
      
      toast.success('Claim rejected successfully');
      fetchClaims(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting claim:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to reject claim';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
      setSelectedClaim(null);
      setRejectionReason('');
    }
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Format the date part (day with ordinal suffix)
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Format the time part (with AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${ordinalSuffix} ${month} ${year} @ ${formattedHours}:${formattedMinutes}${ampm}`;
  };
  
  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Helper to get parsed verification documents
  const getVerificationDocuments = (claim) => {
    if (!claim.verification_documents) return null;
    
    try {
      return typeof claim.parsed_documents !== 'undefined' 
        ? claim.parsed_documents 
        : (typeof claim.verification_documents === 'string' 
          ? JSON.parse(claim.verification_documents) 
          : claim.verification_documents);
    } catch (error) {
      console.error('Error parsing verification documents:', error);
      return null;
    }
  };

  // Function to handle viewing a document with authentication
  const handleViewDocument = async (userId, filename, mimetype) => {
    try {
      setViewingDocument({
        loading: true,
        userId,
        filename,
        mimetype,
        url: null,
        error: null
      });

      // Use the userService to retrieve the document
      const documentBlob = await userService.getVerificationDocument(userId, filename);

      // Create a blob URL for the response
      const blobUrl = URL.createObjectURL(documentBlob);
      
      setViewingDocument({
        loading: false,
        userId,
        filename,
        mimetype,
        url: blobUrl,
        error: null
      });

      // Open document based on mimetype
      if (mimetype?.startsWith('image/') || mimetype === 'application/pdf') {
        // For images and PDFs, open in new tab
        window.open(blobUrl, '_blank');
      } else {
        // For other files, trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        setViewingDocument(null);
      }, 5000);
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Error viewing document: ' + (error.message || 'Please try again.'));
      setViewingDocument({
        loading: false,
        userId,
        filename,
        mimetype,
        url: null,
        error: error.message || 'Failed to load document'
      });
    }
  };
  
  // Helper function to render file previews based on mimetype
  const renderFilePreview = (file, userId) => {
    // Check if there was an error loading this specific file
    const hasError = viewingDocument?.error && 
                     viewingDocument.userId === userId && 
                     viewingDocument.filename === file.filename;
    
    if (hasError) {
      return (
        <div className="h-48 flex flex-col items-center justify-center bg-red-50">
          <div className="mb-2 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="mt-2 text-sm text-red-700">Error loading document</p>
            <p className="mt-1 text-xs text-red-600">{viewingDocument.error}</p>
          </div>
          <button 
            onClick={() => handleViewDocument(userId, file.filename, file.mimetype)}
            className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      );
    }
    
    if (file.mimetype?.startsWith('image/')) {
      return (
        <div className="h-48 flex flex-col items-center justify-center">
          <div className="mb-2 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="mt-1 text-sm text-gray-700">Image</p>
          </div>
          <button 
            onClick={() => handleViewDocument(userId, file.filename, file.mimetype)}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            disabled={viewingDocument?.loading && viewingDocument?.filename === file.filename}
          >
            {viewingDocument?.loading && viewingDocument?.filename === file.filename ? 'Loading...' : 'View Image'}
          </button>
        </div>
      );
    } else if (file.mimetype === 'application/pdf') {
      return (
        <div className="h-48 flex flex-col items-center justify-center">
          <div className="mb-2 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="mt-1 text-sm text-gray-700">PDF Document</p>
          </div>
          <button 
            onClick={() => handleViewDocument(userId, file.filename, file.mimetype)}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            disabled={viewingDocument?.loading && viewingDocument?.filename === file.filename}
          >
            {viewingDocument?.loading && viewingDocument?.filename === file.filename ? 'Loading...' : 'Open PDF'}
          </button>
        </div>
      );
    } else {
      return (
        <div className="h-48 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              {file.mimetype || "Document file"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {(file.size / 1024).toFixed(1)} KB
            </p>
            <button 
              onClick={() => handleViewDocument(userId, file.filename, file.mimetype)}
              className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-800 px-2 py-1 rounded bg-blue-50"
              disabled={viewingDocument?.loading && viewingDocument?.filename === file.filename}
            >
              {viewingDocument?.loading && viewingDocument?.filename === file.filename ? 'Loading...' : 'Download'}
            </button>
          </div>
        </div>
      );
    }
  };
  
  // Toggle expanded view for a claim
  const toggleExpandClaim = (claimId) => {
    setExpandedClaim(expandedClaim === claimId ? null : claimId);
  };
  
  // Helper to list user directory for files
  const getUserFiles = async (userId) => {
    try {
      // List directory for the user (if there's an API endpoint for that)
      // For now, direct admins to check the server directly
      return null;
    } catch (error) {
      console.error('Error checking user files:', error);
      return null;
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Pending Claims</h2>
          <p className="text-sm text-gray-600 mt-1">Loading pending claims...</p>
        </div>
        <div className="flex justify-center p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Pending Claims</h2>
          <p className="text-sm text-gray-600 mt-1">There was an error loading claims</p>
        </div>
        <div className="p-6">
          <div className="mb-4 p-4 border border-red-200 rounded-md bg-red-50">
            <h3 className="text-red-800 font-medium">Error loading claims</h3>
            <p className="text-red-600 mt-1">{error}</p>
          </div>
          <button
            onClick={fetchClaims}
            className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Pending Claims ({claims.length})</h2>
        <p className="text-sm text-gray-600 mt-1">Review and manage pending profile claims</p>
      </div>
      
      {claims.length === 0 ? (
        <div className="p-6 text-center text-gray-600">
          No pending claims to review
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Legal Professional
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SRA Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documents
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {claims.map((claim) => {
                const claimId = `${claim.user_id}-${claim.organisation_id}`;
                const isExpanded = expandedClaim === claimId;
                const verificationDocs = getVerificationDocuments(claim);
                
                return (
                  <React.Fragment key={claimId}>
                    <tr className={`hover:bg-gray-50 ${isExpanded ? 'bg-gray-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{claim.user_name}</div>
                            <div className="text-sm text-gray-500">{claim.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{claim.organisation_name}</div>
                        <div className="text-xs text-gray-500">ID: {claim.organisation_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          {claim.sra_number ? (
                            <div className="text-sm text-gray-900">
                              <span className="font-medium">SRA:</span> {claim.sra_number}
                            </div>
                          ) : null}
                          {claim.authorisation_type ? (
                            <div className="text-xs text-gray-600">
                              {claim.authorisation_type}
                            </div>
                          ) : null}
                          <button 
                            onClick={() => toggleExpandClaim(claimId)}
                            className="mt-1 text-xs text-pink-600 hover:text-pink-800 font-medium"
                          >
                            {isExpanded ? 'Hide details' : 'View details'}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(claim.claim_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {verificationDocs ? (
                          <div>
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {verificationDocs.document_type || 'Documents'}
                            </span>
                            <button 
                              onClick={() => toggleExpandClaim(claimId)}
                              className="block mt-1 text-xs text-pink-600 hover:text-pink-800"
                            >
                              View Docs
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">No documents</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {selectedClaim && selectedClaim.user_id === claim.user_id && processing ? (
                          <span className="text-gray-500">Processing...</span>
                        ) : (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleApprove(claim)}
                              className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => setSelectedClaim(claim)}
                              className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    
                    {/* Expanded details row */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="6" className="px-6 py-4 border-t border-gray-100">
                          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <h4 className="text-lg font-medium text-gray-900 mb-3">
                              SRA Verification Details
                            </h4>
                            
                            {/* Document Files Section */}
                            <div className="mt-6 border-t border-gray-200 pt-4">
                              <h5 className="text-sm font-medium text-gray-700 mb-3">Uploaded Document Files</h5>
                              
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex flex-col">
                                  {/* Look for files in parsed_documents.files first */}
                                  {claim.parsed_documents?.files?.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {claim.parsed_documents.files.map((file, index) => (
                                        <div key={index} className="border border-gray-200 rounded-md overflow-hidden bg-white">
                                          <div className="p-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                                            <span className="text-sm font-medium truncate flex-1">
                                              {file.originalName || file.filename}
                                            </span>
                                            <button
                                              onClick={() => handleViewDocument(claim.user_id, file.filename, file.mimetype)}
                                              className="ml-2 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded bg-blue-50"
                                              disabled={viewingDocument?.loading && viewingDocument?.filename === file.filename}
                                            >
                                              {viewingDocument?.loading && viewingDocument?.filename === file.filename ? 'Loading...' : 'View'}
                                            </button>
                                          </div>
                                          <div className="p-3">
                                            {renderFilePreview(file, claim.user_id)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-center py-8 bg-blue-50 rounded-lg border border-blue-100">
                                      <div className="mx-auto h-12 w-12 text-blue-400 mb-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                                        </svg>
                                      </div>
                                      <p className="text-sm text-gray-700">
                                        Check for documents in the server uploads directory:
                                      </p>
                                      <p className="font-mono text-xs bg-gray-800 text-white py-1 px-2 rounded mt-2 inline-block">
                                        server/src/uploads/user_{claim.user_id}/
                                      </p>
                                      <p className="mt-3 text-sm text-blue-700 font-medium">
                                        These documents will be displayed here after refreshing the page.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-4 space-y-2">
                              <h5 className="text-sm font-medium text-gray-700">Notes:</h5>
                              <p className="text-sm text-gray-600">
                                Verify that the submitted information matches the SRA database records before approving.
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Rejection Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Reject Profile Claim Request
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Please provide a reason for rejection.
              </p>
            </div>
            <div className="px-6 py-4">
              <label 
                htmlFor="rejectionReason" 
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Rejection Reason
              </label>
              <textarea
                id="rejectionReason"
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                rows="4"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this claim..."
              ></textarea>
            </div>
            <div className="px-6 py-3 bg-gray-50 flex justify-end space-x-3">
              <button
                type="button"
                className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                onClick={() => {
                  setSelectedClaim(null);
                  setRejectionReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={() => handleReject(selectedClaim)}
                disabled={processing}
              >
                {processing ? 'Rejecting...' : 'Reject Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsList; 